// Tall-obstacle map symbols. Follows the FAA VFR sectional convention
// for plotted obstructions: a thin vertical mast with a flag-like
// marker at the top. Lighted obstacles get a small ember dot above the
// marker to mimic the chart's "lighted obstruction" black-dot.
//
// We register FOUR variants and pick at draw time via the symbol
// layer's icon-image expression:
//   obs-plain      unlighted, < 1000 ft AGL
//   obs-lit        lighted,   < 1000 ft AGL
//   obs-plain-tall unlighted, >= 1000 ft AGL  (heavier mast + outline)
//   obs-lit-tall   lighted,   >= 1000 ft AGL
//
// Visual treatment (revised 2026-05-14 per Aaron field report: the
// yellow mast disappeared against the VFR sectional's tan/yellow
// background). Updates:
//   - SIZE bumped from 22 → 30 so the icon reads at a glance.
//   - Mast + arm rendered in dark slate with a white outline halo
//     instead of yellow-on-yellow. Matches the actual FAA sectional
//     obstruction symbol (dark obstruction icon) better than the
//     old yellow rendering did.
//   - Slight magenta tint on the flag arm of TALL obstacles so they
//     pop further — tall obstacles are the operationally critical
//     ones (>=1000 AGL = chart-significant on a VFR sectional).

import type { Map as MlMap } from 'maplibre-gl';
import { ensureSvgImages } from './iconRegistry';

const SIZE = 30;

interface Variant {
  id: string;
  tall: boolean;
  lit: boolean;
}

const VARIANTS: Variant[] = [
  { id: 'obs-plain',      tall: false, lit: false },
  { id: 'obs-lit',        tall: false, lit: true  },
  { id: 'obs-plain-tall', tall: true,  lit: false },
  { id: 'obs-lit-tall',   tall: true,  lit: true  },
];

function svgFor(v: Variant): string {
  // Anchor at the base of the mast (this is where the obstacle lat/lon
  // actually points to). Flag arm angles to the right so we don't
  // overlap typical map labels on the left of an airport's ident.
  const cx = SIZE / 2;
  const baseY = SIZE - 3;
  const topY = v.tall ? 4 : 7;
  const mastW = v.tall ? 3.6 : 2.8;
  const haloW = mastW + 3.0;

  // White halo behind the mast for contrast on any basemap — reads
  // against the sectional's tan/yellow as well as against satellite
  // and IFR low's gray-blue.
  const haloRect = `<rect x="${cx - haloW / 2}" y="${topY - 1.4}" width="${haloW}" height="${baseY - topY + 2.8}" fill="#f9fafb" rx="0.8"/>`;
  // Main mast in dark slate so it reads on yellow sectional. The FAA's
  // actual sectional obstruction symbol is dark; the old yellow mast
  // came from a misread of the chart convention.
  const mastColor = v.tall ? '#0b0d0f' : '#1f2937';
  const mastRect = `<rect x="${cx - mastW / 2}" y="${topY}" width="${mastW}" height="${baseY - topY}" fill="${mastColor}"/>`;

  // Flag arm at the top, pointing right — a small filled triangle.
  // Tall obstacles get a magenta tint so they pop further — these
  // are the chart-significant ones (>=1000 AGL).
  const armColor = v.tall ? '#e11d48' : '#1f2937';
  const armLen = v.tall ? 11 : 9;
  const armY = topY + 3.0;
  const armTip = cx + armLen;
  const arm = `
    <polygon points="${cx},${topY} ${armTip.toFixed(1)},${(topY + 1.4).toFixed(1)} ${cx},${armY.toFixed(1)}" fill="${armColor}" stroke="#f9fafb" stroke-width="0.8" stroke-linejoin="round"/>
  `;

  // Ground line — short horizontal at the base, like the chart symbol.
  const groundHalf = v.tall ? 5.5 : 4;
  const ground = `<rect x="${cx - groundHalf}" y="${baseY}" width="${groundHalf * 2}" height="2.2" fill="${mastColor}" stroke="#f9fafb" stroke-width="0.6"/>`;

  // Lighted: a red dot above the flag arm. The FAA chart version is
  // a tiny black dot; we use red so it reads as "lit" at a glance
  // even at the icon's reduced render size.
  const litDot = v.lit
    ? `<circle cx="${cx}" cy="${(topY - 2.4).toFixed(1)}" r="2.2" fill="#ef4444" stroke="#f9fafb" stroke-width="0.8"/>`
    : '';

  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="${SIZE}" height="${SIZE}" viewBox="0 0 ${SIZE} ${SIZE}">
      ${haloRect}
      ${mastRect}
      ${arm}
      ${ground}
      ${litDot}
    </svg>
  `.trim();
}

const SYMBOLS = VARIANTS.map((v) => ({ id: v.id, svg: svgFor(v) }));

/** Style-expression that picks the right icon per feature.
 *
 * 'lit' is true when the lighting code is anything except 'N' (none)
 * or 'U' (unknown). 'tall' threshold is 1000 ft AGL \u2014 matches the
 * sectional symbol break and roughly aligns with FAR 77 obstruction-
 * marking standards. */
export const OBSTACLE_ICON_EXPRESSION: any = [
  'case',
  [
    'all',
    ['>=', ['coalesce', ['get', 'agl'], 0], 1000],
    [
      'all',
      ['!=', ['coalesce', ['get', 'lighting'], 'U'], 'N'],
      ['!=', ['coalesce', ['get', 'lighting'], 'U'], 'U'],
    ],
  ],
  'obs-lit-tall',
  [
    'all',
    ['>=', ['coalesce', ['get', 'agl'], 0], 1000],
  ],
  'obs-plain-tall',
  [
    'all',
    ['!=', ['coalesce', ['get', 'lighting'], 'U'], 'N'],
    ['!=', ['coalesce', ['get', 'lighting'], 'U'], 'U'],
  ],
  'obs-lit',
  'obs-plain',
];

export function ensureObstacleIcons(map: MlMap): Promise<void> {
  return ensureSvgImages(map, SYMBOLS, {
    defaultSize: SIZE,
    logTag: '[obstacleIcons]',
    pixelRatioCap: 2,
  });
}
